// Owner: shell-nav (plan §9.2, interaction-systems F18). One banner instead of endless spinners: when
// requests fail because the API cannot be reached (network error, 502, 503, 504), the shell says so
// within seconds, checks again every 5 s, retries what failed once it is back, and offers Retry now.
// A request that runs longer than 10 s gets a quiet "This takes longer than usual" line, so no spinner
// stays without words.
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isPlainRecord } from '@dataroom/core';
import { Banner, Button, Spinner } from '@dataroom/ui';
import { t } from '../lib/i18n';
import { onRequestOutcome } from '../lib/queries';

const CHECK_EVERY_MS = 5_000;
const SLOW_AFTER_MS = 10_000;

/** Whether an error means "the server can't be reached" (not "the server said no"). */
export function isUnreachable(error: unknown): boolean {
  const status: unknown = isPlainRecord(error) ? error.status : undefined;
  if (typeof status === 'number') return status === 0 || status === 502 || status === 503 || status === 504;
  if (error instanceof TypeError) return true; // fetch: "Failed to fetch", "NetworkError …", "Load failed"
  const message: unknown = isPlainRecord(error)
    ? error.message
    : error instanceof Error
      ? error.message
      : undefined;
  return typeof message === 'string' && /failed to fetch|networkerror|load failed/i.test(message);
}

async function serverReachable(signal?: AbortSignal): Promise<boolean> {
  try {
    const res = await fetch('/api/v1/health', { cache: 'no-store', ...(signal ? { signal } : {}) });
    return res.ok;
  } catch {
    return false;
  }
}

function refetchFailed(qc: QueryClient) {
  void qc.refetchQueries({ type: 'active', predicate: (q) => q.state.status === 'error' });
}

export type ConnectivityState = { unreachable: boolean; slow: boolean; checking: boolean; retry: () => void };

/** The connectivity of the API as the app experiences it. */
export function useConnectivity(): ConnectivityState {
  const qc = useQueryClient();
  const [unreachable, setUnreachable] = useState(false);
  const [checking, setChecking] = useState(false);
  const [slow, setSlow] = useState(false);
  const unreachableRef = useRef(false);
  unreachableRef.current = unreachable;
  const inFlight = useRef<Promise<void> | null>(null);

  // Ask the health endpoint before saying anything: one endpoint answering 503 is that screen's
  // error, an API that does not answer at all is the shell's.
  const check = useCallback(
    (signal?: AbortSignal): Promise<void> => {
      inFlight.current ??= (async () => {
        setChecking(true);
        const ok = await serverReachable(signal);
        inFlight.current = null;
        if (signal?.aborted) return;
        setChecking(false);
        if (ok) {
          if (unreachableRef.current) refetchFailed(qc);
          setUnreachable(false);
        } else {
          setUnreachable(true);
        }
      })();
      return inFlight.current;
    },
    [qc],
  );

  useEffect(
    () =>
      onRequestOutcome(({ ok, error }) => {
        if (!ok) {
          if (isUnreachable(error)) void check();
        } else if (unreachableRef.current) {
          setUnreachable(false);
        }
      }),
    [check],
  );

  // While unreachable: check every 5 s.
  useEffect(() => {
    if (!unreachable) return;
    const ctl = new AbortController();
    const id = window.setInterval(() => void check(ctl.signal), CHECK_EVERY_MS);
    return () => {
      ctl.abort();
      window.clearInterval(id);
    };
  }, [unreachable, check]);

  // The browser knows first when the network goes away or comes back.
  useEffect(() => {
    const offline = () => setUnreachable(true);
    const online = () => void check();
    window.addEventListener('offline', offline);
    window.addEventListener('online', online);
    return () => {
      window.removeEventListener('offline', offline);
      window.removeEventListener('online', online);
    };
  }, [check]);

  // Slow requests: a query fetching for more than 10 s.
  useEffect(() => {
    const started = new Map<string, number>();
    const cache = qc.getQueryCache();
    const unsubscribe = cache.subscribe((event) => {
      const q = event.query;
      if (q.state.fetchStatus === 'fetching') {
        if (!started.has(q.queryHash)) started.set(q.queryHash, performance.now());
      } else started.delete(q.queryHash);
    });
    const id = window.setInterval(() => {
      const now = performance.now();
      setSlow([...started.values()].some((at) => now - at > SLOW_AFTER_MS));
    }, 1_000);
    return () => {
      unsubscribe();
      window.clearInterval(id);
    };
  }, [qc]);

  return { unreachable, slow, checking, retry: () => void check() };
}

/** The banner at the top of the shell's content area. */
export function ConnectivityBanner() {
  const { unreachable, slow, checking, retry } = useConnectivity();
  if (unreachable)
    return (
      <div className="shell-banner" data-testid="api-unreachable">
        <Banner
          tone="err"
          icon="alert"
          title={t('shell.offline.title')}
          action={
            <Button size="sm" variant="secondary" loading={checking} onClick={retry} data-testid="api-retry">
              {t('shell.offline.retry')}
            </Button>
          }
        >
          {t('shell.offline.body')}
        </Banner>
      </div>
    );
  if (slow)
    return (
      <div className="shell-slow" role="status" data-testid="api-slow">
        <Spinner size={12} />
        {t('shell.slow.body')}
      </div>
    );
  return null;
}
